import React, { useState, useEffect } from "react";
import { safeFormat } from "../../../../utils/financialHelper";
import "./assumption.less";
import { useReitsValuationState } from "../../../../context/reitsValuationContext";
import { updateCalculatedValues } from "../../../../services/cashflow-service";
import { formatNumber } from "../../../../utils/financialHelper";
import {
  calculateCostOfDebt,
  calculateCostOfEquity,
  calculateXNPV,
} from "../../../../services/wacc-service";

type AssumptionProps = {
  onClose: () => void;
  selectedLocation: string;
};

export default function Assumption({
  onClose,
  selectedLocation,
}: AssumptionProps) {
  console.log("selected location in assumption component : ", selectedLocation);
  const [activeTab, setActiveTab] = useState<"cashflow" | "wacc">("cashflow");

  const { evalState, setEvalState } = useReitsValuationState();

  const locationOptions = [
    { label: "Mumbai", value: 0.5 },
    { label: "Bengaluru", value: 0.5 },
    { label: "Chennai", value: 0.5 },
    { label: "Hyderabad", value: 0.75 },
    { label: "Pune", value: 0.5 },
    { label: "NCR", value: 0.75 },
    { label: "Ahmedabad", value: 0.5 },
    { label: "Amritsar", value: 0.5 },
    { label: "Bhubaneswar", value: 0.5 },
    { label: "Chandigarh", value: 0.5 },
    { label: "Delhi", value: 0.75 },
    { label: "Gurugram", value: 0.75 },
    { label: "Indore", value: 0.5 },
    { label: "Kolkata", value: 0.5 },
    { label: "Mangaluru", value: 0.5 },
    { label: "Mysuru", value: 0.5 },
    { label: "Noida", value: 0.75 },
    { label: "Udaipur", value: 0.5 },
    { label: "Navi Mumbai", value: 0.5 },
    { label: "Karnataka", value: 0.5 },
  ];

  // Helper function to find location value from label
  const getLocationValueFromLabel = (label: string): number => {
    const location = locationOptions.find((opt) => opt.label === label);
    return location ? location.value : 0.5; // default to 0.5
  };

  // Initialize selectedLocationLabel from prop first, then fallback to evalState
  const [selectedLocationLabel, setSelectedLocationLabel] = useState<string>(
    selectedLocation || "Mumbai"
  );

  const [riskInputs, setRiskInputs] = useState({
    location: getLocationValueFromLabel(selectedLocation || "Mumbai"),
    tenantProfile:
      typeof evalState.tenantProfile === "number" &&
      !isNaN(evalState.tenantProfile)
        ? evalState.tenantProfile
        : 0.25,
    wale:
      typeof evalState.wale === "number" && !isNaN(evalState.wale)
        ? evalState.wale
        : 0.25,
    buildingLife:
      typeof evalState.buildingLife === "number" &&
      !isNaN(evalState.buildingLife)
        ? evalState.buildingLife
        : 0.25,
    vacancy:
      typeof evalState.vacancy === "number" && !isNaN(evalState.vacancy)
        ? evalState.vacancy
        : 0.25,
    propertyType:
      typeof evalState.propertyType === "number" &&
      !isNaN(evalState.propertyType)
        ? evalState.propertyType
        : 0.1,
    esgCompliance:
      typeof evalState.esgCompliance === "number" &&
      !isNaN(evalState.esgCompliance)
        ? evalState.esgCompliance
        : -0.5,
  });

  const [waccInputs, setWaccInputs] = useState({
    costDebt:
      typeof evalState.costOfDebt === "number" &&
      !isNaN(evalState.costOfDebt) &&
      evalState.costOfDebt > 0
        ? evalState.costOfDebt
        : calculateCostOfDebt(),
    weightDebt:
      typeof evalState.weightOfDebt === "number" &&
      !isNaN(evalState.weightOfDebt)
        ? evalState.weightOfDebt
        : 50,
    costEquity:
      typeof evalState.costOfEquity === "number" &&
      !isNaN(evalState.costOfEquity) &&
      evalState.costOfEquity > 0
        ? evalState.costOfEquity
        : calculateCostOfEquity(),
    weightEquity:
      typeof evalState.weightOfEquity === "number" &&
      !isNaN(evalState.weightOfEquity)
        ? evalState.weightOfEquity
        : 50,
  });

  const [totalWacc, setTotalWacc] = useState(0);

  // Update local state when evalState changes (e.g., when API data loads)
  useEffect(() => {
    if (
      evalState.costOfDebt &&
      !isNaN(evalState.costOfDebt) &&
      evalState.costOfDebt > 0
    ) {
      setRiskInputs({
        location: getLocationValueFromLabel(selectedLocation || "Mumbai"),
        tenantProfile:
          typeof evalState.tenantProfile === "number" &&
          !isNaN(evalState.tenantProfile)
            ? evalState.tenantProfile
            : 0.25,
        wale:
          typeof evalState.wale === "number" && !isNaN(evalState.wale)
            ? evalState.wale
            : 0.25,
        buildingLife:
          typeof evalState.buildingLife === "number" &&
          !isNaN(evalState.buildingLife)
            ? evalState.buildingLife
            : 0.25,
        vacancy:
          typeof evalState.vacancy === "number" && !isNaN(evalState.vacancy)
            ? evalState.vacancy
            : 0.25,
        propertyType:
          typeof evalState.propertyType === "number" &&
          !isNaN(evalState.propertyType)
            ? evalState.propertyType
            : 0.1,
        esgCompliance:
          typeof evalState.esgCompliance === "number" &&
          !isNaN(evalState.esgCompliance)
            ? evalState.esgCompliance
            : -0.5,
      });

      setSelectedLocationLabel(selectedLocation || "Mumbai");

      setWaccInputs({
        costDebt: evalState.costOfDebt,
        weightDebt:
          typeof evalState.weightOfDebt === "number" &&
          !isNaN(evalState.weightOfDebt)
            ? evalState.weightOfDebt
            : 50,
        costEquity: evalState.costOfEquity,
        weightEquity:
          typeof evalState.weightOfEquity === "number" &&
          !isNaN(evalState.weightOfEquity)
            ? evalState.weightOfEquity
            : 50,
      });
    }
  }, [
    evalState.costOfDebt,
    evalState.costOfEquity,
    evalState.tenantProfile,
    evalState.wale,
    evalState.buildingLife,
    evalState.vacancy,
    evalState.propertyType,
    evalState.esgCompliance,
    evalState.weightOfDebt,
    evalState.weightOfEquity,
    selectedLocation,
  ]);

  // Auto-adjust equity weight when debt weight changes
  useEffect(() => {
    const totalWeight = waccInputs.weightDebt + waccInputs.weightEquity;
    if (totalWeight !== 100) {
      const adjustedWeightEquity = 100 - waccInputs.weightDebt;
      setWaccInputs((prev) => ({
        ...prev,
        weightEquity: adjustedWeightEquity,
      }));
    }
  }, [waccInputs.weightDebt]);

  // Calculate WACC for display only (doesn't update context)
  useEffect(() => {
    const baseWacc =
      (waccInputs.costDebt * waccInputs.weightDebt +
        waccInputs.costEquity * waccInputs.weightEquity) /
      100;

    const riskPremium = calculateRiskPremium();

    const finalWacc = baseWacc + riskPremium;

    setTotalWacc(isNaN(finalWacc) ? 0 : finalWacc);
  }, [waccInputs, riskInputs]);

  const handleRiskChange = (field: keyof typeof riskInputs, value: number) => {
    setRiskInputs({ ...riskInputs, [field]: value });
  };

  const handleLocationChange = (label: string) => {
    const selectedOption = locationOptions.find((opt) => opt.label === label);
    if (selectedOption) {
      setSelectedLocationLabel(label);
      setRiskInputs({ ...riskInputs, location: selectedOption.value });
    }
  };

  const calculateRiskPremium = () => {
    const total =
      Number(riskInputs.location || 0) +
      Number(riskInputs.tenantProfile || 0) +
      Number(riskInputs.wale || 0) +
      Number(riskInputs.buildingLife || 0) +
      Number(riskInputs.vacancy || 0) +
      Number(riskInputs.propertyType || 0) +
      Number(riskInputs.esgCompliance || 0);

    return parseFloat(total.toFixed(2));
  };

  const handleCashFlowChange = (
    rowIndex: number,
    colIndex: number,
    value: string
  ) => {
    let numericValue = parseFloat(value);

    // Prevent invalid input like NaN or empty
    if (isNaN(numericValue)) numericValue = 0;

    // Validation rules
    const rowLabel =
      evalState.cashFlowData[rowIndex]?.label?.toLowerCase() || "";

    // Occupancy / Escalation must be between 0–100%
    if (
      (rowLabel.includes("occupancy") || rowLabel.includes("escalation")) &&
      (numericValue < 0 || numericValue > 100)
    ) {
      alert("Value must be between 0% and 100%");
      return;
    }

    // Average Rental must be positive
    if (rowLabel.includes("average rental") && numericValue <= 0) {
      alert("Average Rental should be positive.");
      return;
    }

    // Proceed with update
    const changeData = [...evalState.cashFlowData];
    changeData[rowIndex].values[colIndex] = numericValue;

    const newData = updateCalculatedValues(
      changeData,
      evalState.columns,
      evalState.noiPercentage,
      true,
      evalState.capRate
    );

    setEvalState((prev) => ({ ...prev, cashFlowData: newData }));
  };

  const handleWaccChange = (field: keyof typeof waccInputs, value: number) => {
    setWaccInputs({ ...waccInputs, [field]: value });
  };

  const handleCalculate = () => {
    const baseWacc =
      (waccInputs.costDebt * waccInputs.weightDebt +
        waccInputs.costEquity * waccInputs.weightEquity) /
      100;

    const riskPremium = calculateRiskPremium();

    const totalDiscountRate = totalWacc + riskPremium;

    const valuation = parseFloat(
      calculateXNPV(
        totalDiscountRate,
        evalState.cashFlowData[5]?.values.slice(1) || [],
        evalState.columns
      ).toFixed(2)
    );

    setEvalState((prev) => ({
      ...prev,
      costOfDebt: waccInputs.costDebt,
      weightOfDebt: waccInputs.weightDebt,
      costOfEquity: waccInputs.costEquity,
      weightOfEquity: waccInputs.weightEquity,

      location: riskInputs.location,
      tenantProfile: riskInputs.tenantProfile,
      wale: riskInputs.wale,
      buildingLife: riskInputs.buildingLife,
      vacancy: riskInputs.vacancy,
      propertyType: riskInputs.propertyType,
      esgCompliance: riskInputs.esgCompliance,

      riskPremium: riskPremium,
      waccRange: parseFloat(baseWacc.toFixed(2)),
      waccDiscountRateUser: parseFloat(totalDiscountRate.toFixed(2)),
      valuationUser: valuation,
    }));

    onClose();
  };

  return (
    <div className="assumption">
      <div className="assumption__overlay" onClick={onClose}></div>
      <div className="assumption__content">
        {/* Header */}
        <div className="assumption__header">
          <h2 className="assumption__title">Based on Users Assumptions</h2>
        </div>

        {/* Tabs */}
        <div className="assumption__tabs">
          <button
            className={`assumption__tab ${
              activeTab === "cashflow" ? "active" : ""
            }`}
            onClick={() => setActiveTab("cashflow")}
          >
            Cash flow based on different scenarios
          </button>
          <button
            className={`assumption__tab ${
              activeTab === "wacc" ? "active" : ""
            }`}
            onClick={() => setActiveTab("wacc")}
          >
            WACC Calculation
          </button>
        </div>

        {/* Cashflow Tab */}
        {activeTab === "cashflow" && (
          <div className="assumption__body">
            <div className="assumption__table-wrapper">
              <table className="assumption__table">
                <thead>
                  <tr>
                    <th>Financial Year</th>
                    {evalState.columns.map((col, i) => (
                      <th key={i}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {evalState.cashFlowData.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td>
                        {row.label.startsWith("NOI @") ? (
                          <>
                            NOI @
                            <input
                              type="number"
                              value={evalState.noiPercentage}
                              onChange={(e) => {
                                const newNOIPercentage = parseFloat(
                                  e.target.value
                                );

                                // Validate that NOI input is positive
                                if (
                                  isNaN(newNOIPercentage) ||
                                  newNOIPercentage < 0
                                ) {
                                  alert(
                                    "NOI percentage must be a positive number."
                                  );
                                  return;
                                }

                                const updatedData = updateCalculatedValues(
                                  evalState.cashFlowData,
                                  evalState.columns,
                                  newNOIPercentage,
                                  false,
                                  evalState.capRate
                                );

                                setEvalState((prev) => ({
                                  ...prev,
                                  noiPercentage: newNOIPercentage,
                                  cashFlowData: updatedData,
                                }));
                              }}
                              style={{
                                width: "50px",
                                marginLeft: "5px",
                                marginRight: "5px",
                              }}
                            />
                            %
                          </>
                        ) : (
                          row.label
                        )}
                      </td>
                      {row.values.map((value: number, colIndex: number) => (
                        <td key={`${rowIndex}-${colIndex}`}>
                          {rowIndex === 1 ||
                          rowIndex === 2 ||
                          (rowIndex === 3 && colIndex !== 0) ? (
                            <input
                              type="number"
                              value={formatNumber(value)}
                              onChange={(e) =>
                                handleCashFlowChange(
                                  rowIndex,
                                  colIndex,
                                  e.target.value
                                )
                              }
                            />
                          ) : (
                            <span>{formatNumber(value)}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cap Rate */}
            <div className="assumption__caprate">
              <label>Cap Rate</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={evalState.capRate || ""}
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "") {
                    setEvalState((prev) => ({
                      ...prev,
                      capRate: NaN,
                    }));
                    return;
                  }

                  let _capRate = parseFloat(value);
                  if (isNaN(_capRate)) return;

                  setEvalState((prev) => {
                    // Check prev.capRate here
                    if (prev.capRate === 0) {
                      _capRate = _capRate; // actually redundant, you can remove this line
                    }

                    const updatedData = updateCalculatedValues(
                      prev.cashFlowData,
                      prev.columns,
                      prev.noiPercentage,
                      false,
                      _capRate
                    );

                    return {
                      ...prev,
                      capRate: _capRate,
                      cashFlowData: updatedData,
                    };
                  });
                }}
              />
            </div>
          </div>
        )}

        {/* WACC Tab */}
        {activeTab === "wacc" && (
          <div className="assumption__wacc-grid">
            {/* Left - Risk Premium */}
            <div className="assumption__wacc-left">
              <h3>Risk Premium</h3>

              <div className="assumption__field">
                <label>Location</label>
                <select
                  value={selectedLocationLabel}
                  onChange={(e) => handleLocationChange(e.target.value)}
                >
                  {locationOptions.map((option, index) => (
                    <option key={index} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="assumption__field">
                <label>Tenant Profile</label>
                <select
                  value={riskInputs.tenantProfile}
                  onChange={(e) =>
                    handleRiskChange("tenantProfile", Number(e.target.value))
                  }
                >
                  <option value={0.25}>Strong</option>
                  <option value={0.5}>Moderate</option>
                  <option value={0.75}>Weak</option>
                </select>
              </div>

              <div className="assumption__field">
                <label>WALE</label>
                <select
                  value={riskInputs.wale}
                  onChange={(e) =>
                    handleRiskChange("wale", Number(e.target.value))
                  }
                >
                  <option value={0.25}>More than 5</option>
                  <option value={0.5}>Between 2-5</option>
                  <option value={0.75}>Less than 2</option>
                </select>
              </div>

              <div className="assumption__field">
                <label>Life of the building</label>
                <select
                  value={riskInputs.buildingLife}
                  onChange={(e) =>
                    handleRiskChange("buildingLife", Number(e.target.value))
                  }
                >
                  <option value={0.25}>Less than 10 years</option>
                  <option value={0.75}>Between 10-20 years</option>
                  <option value={1}>More than 20 years</option>
                </select>
              </div>

              <div className="assumption__field">
                <label>Vacancy</label>
                <select
                  value={riskInputs.vacancy}
                  onChange={(e) =>
                    handleRiskChange("vacancy", Number(e.target.value))
                  }
                >
                  <option value={0.25}>Less than 10%</option>
                  <option value={0.5}>Between 10-25%</option>
                  <option value={1}>More than 25%</option>
                </select>
              </div>

              <div className="assumption__field">
                <label>Type of property</label>
                <select
                  value={riskInputs.propertyType}
                  onChange={(e) =>
                    handleRiskChange("propertyType", Number(e.target.value))
                  }
                >
                  <option value={0.1}>Grade A</option>
                  <option value={1}>Grade B</option>
                </select>
              </div>

              <div className="assumption__field">
                <label>ESG Compliance</label>
                <select
                  value={riskInputs.esgCompliance}
                  onChange={(e) =>
                    handleRiskChange("esgCompliance", Number(e.target.value))
                  }
                >
                  <option value={-0.5}>Good/LEED Platinum or equivalent</option>
                  <option value={-0.25}>Average/LEED Gold or equivalent</option>
                  <option value={0}>Poor/No certification</option>
                </select>
              </div>

              <div className="assumption__premium">
                <span>Add: Risk Premium</span>
                <strong>{calculateRiskPremium()}%</strong>
              </div>
            </div>

            {/* Right - WACC Sliders */}
            <div className="assumption__wacc-right">
              <div className="assumption__slider">
                <label>Cost of Debt (%)</label>
                <div className="assumption__slider-row">
                  <input
                    type="range"
                    min="2"
                    max="10"
                    step="0.1"
                    value={waccInputs.costDebt}
                    style={
                      {
                        "--value": `${
                          ((waccInputs.costDebt - 2) / (10 - 2)) * 100
                        }%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) =>
                      handleWaccChange("costDebt", Number(e.target.value))
                    }
                  />
                  <input
                    type="number"
                    min="2"
                    max="10"
                    step="0.1"
                    value={waccInputs.costDebt.toFixed(2)}
                    onChange={(e) =>
                      handleWaccChange("costDebt", Number(e.target.value))
                    }
                    className="assumption__slider-input"
                  />
                </div>
              </div>

              <div className="assumption__slider">
                <label>Weight of Debt (%)</label>
                <div className="assumption__slider-row">
                  <input
                    type="range"
                    min={1}
                    max={100}
                    step={1}
                    value={waccInputs.weightDebt}
                    style={
                      {
                        "--value": `${
                          ((waccInputs.weightDebt - 1) / (100 - 1)) * 100
                        }%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) =>
                      handleWaccChange("weightDebt", Number(e.target.value))
                    }
                  />
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={waccInputs.weightDebt}
                    onChange={(e) =>
                      handleWaccChange("weightDebt", Number(e.target.value))
                    }
                    className="assumption__slider-input"
                  />
                </div>
              </div>

              <div className="assumption__slider">
                <label>Cost of Equity (%)</label>
                <div className="assumption__slider-row">
                  <input
                    type="range"
                    min="8"
                    max="17"
                    step="0.1"
                    value={waccInputs.costEquity}
                    style={
                      {
                        "--value": `${
                          ((waccInputs.costEquity - 8) / (17 - 8)) * 100
                        }%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) =>
                      handleWaccChange("costEquity", Number(e.target.value))
                    }
                  />
                  <input
                    type="number"
                    min="8"
                    max="17"
                    step="0.1"
                    value={waccInputs.costEquity.toFixed(2)}
                    onChange={(e) =>
                      handleWaccChange("costEquity", Number(e.target.value))
                    }
                    className="assumption__slider-input"
                  />
                </div>
              </div>

              <div className="assumption__slider">
                <label>Weight of Equity (%)</label>
                <div className="assumption__slider-row">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    step="1"
                    value={waccInputs.weightEquity}
                    style={
                      {
                        "--value": `${
                          ((waccInputs.weightEquity - 1) / (100 - 1)) * 100
                        }%`,
                      } as React.CSSProperties
                    }
                    onChange={(e) => {
                      const newWeightEquity = Number(e.target.value);
                      handleWaccChange("weightEquity", newWeightEquity);
                      handleWaccChange("weightDebt", 100 - newWeightEquity);
                    }}
                  />
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={waccInputs.weightEquity}
                    onChange={(e) => {
                      const newWeightEquity = Number(e.target.value);
                      handleWaccChange("weightEquity", newWeightEquity);
                      handleWaccChange("weightDebt", 100 - newWeightEquity);
                    }}
                    className="assumption__slider-input"
                  />
                </div>
              </div>

              <div className="assumption__wacc-display">
                <span>WACC</span>
                <strong>{totalWacc.toFixed(2)}%</strong>
              </div>
            </div>

            <div className="assumption__discount">
              <span>Total Discount Rate</span>
              <strong>
                {(totalWacc + calculateRiskPremium()).toFixed(2)}%
              </strong>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="assumption__footer">
          <button
            className="assumption__btn assumption__btn--secondary"
            onClick={onClose}
          >
            Cancel
          </button>

          {activeTab === "wacc" && (
            <button
              className="assumption__btn assumption__btn--secondary"
              onClick={() => setActiveTab("cashflow")}
            >
              Back
            </button>
          )}

          <button
            className="assumption__btn assumption__btn--primary"
            onClick={() => {
              if (activeTab === "cashflow") {
                setActiveTab("wacc");
              } else {
                handleCalculate();
              }
            }}
          >
            {activeTab === "cashflow" ? "Continue" : "Calculate"}
          </button>
        </div>
      </div>
    </div>
  );
}